import * as React from "react"
import { t } from "ttag"
import { BaseProps, Caption, CompletionChart, MetricsCard, NumberMetric, Skeletonable } from "@humanpredictiveintelligence/myqvt-library"

import { SurveyContentType } from "models/generated"
import { useGlobalOverallCommentsCountQuery } from "graphql/queries/generated/GlobalOverallCommentsCount"
import {
  useGlobalOverallCommentsCountDeltaQuery,
} from "graphql/queries/generated/GlobalOverallCommentsCountDelta"
import { useGlobalOverallResponsesCountQuery } from "graphql/queries/generated/GlobalOverallResponsesCount"
import {
  useGlobalResponsesToCommentsRatioQuery,
} from "graphql/queries/generated/GlobalResponsesToCommentsRatio"
import {
  useGlobalResponsesToCommentsRatioDeltaQuery,
} from "graphql/queries/generated/GlobalResponsesToCommentsRatioDelta"

export const CommentsMetrics: React.FC<Skeletonable<CommentsMetricsProps>> = (props) => {
  const { data: commentsCountData, loading: isCommentsCountLoading } = useGlobalOverallCommentsCountQuery({
    variables: {
      isUserAllowedToReadComments: props.isUserAllowedToReadComments,
      surveyContentType: props.surveyContentType,
    },
  })
  const { data: commentsCountDeltaData, loading: isCommentsCountDeltaLoading } =
    useGlobalOverallCommentsCountDeltaQuery({
      variables: {
        isUserAllowedToReadComments: props.isUserAllowedToReadComments,
        surveyContentType: props.surveyContentType,
      },
    })
  const { data: responsesCountData, loading: isReponsesCountLoading } = useGlobalOverallResponsesCountQuery({
    variables: {
      isUserAllowedToReadComments: props.isUserAllowedToReadComments,
      surveyContentType: props.surveyContentType,
    },
  })
  const {
    data: responsesToCommentsRatioData,
    loading: isResponsesToCommentsRatioLoading,
  } = useGlobalResponsesToCommentsRatioQuery({
    variables: {
      isUserAllowedToReadComments: props.isUserAllowedToReadComments,
      surveyContentType: props.surveyContentType,
    },
  })
  const {
    data: responsesToCommentsRatioDeltaData,
    loading: isResponsesToCommentsRatioDeltaLoading,
  } = useGlobalResponsesToCommentsRatioDeltaQuery({
    variables: {
      isUserAllowedToReadComments: props.isUserAllowedToReadComments,
      surveyContentType: props.surveyContentType,
    },
  })

  const overallCommentsCount = commentsCountData?.commentsStatistics.overallCommentsCount
  const rawOverallCommentsCountDelta = commentsCountDeltaData?.commentsStatistics.overallCommentsCountDelta
  const overallResponsesCount = responsesCountData?.commentsStatistics.overallResponsesCount
  const responsesToCommentsRatio = responsesToCommentsRatioData?.commentsStatistics.responsesToCommentsRatio
  const responsesToCommentsRatioDelta =
    responsesToCommentsRatioDeltaData?.commentsStatistics.responsesToCommentsRatioDelta

  const overallCommentsCountDelta
    = (rawOverallCommentsCountDelta && "+" + rawOverallCommentsCountDelta) || undefined

  let averageCommentsCountPerQuestionDelta
  if (props.averageCommentsCountPerQuestionTrend) {
    averageCommentsCountPerQuestionDelta
      = props.averageCommentsCountPerQuestionTrend === "positive"
        ? t`increasing`
        : t`decreasing`
  }

  const responseToCommentRatio = responsesToCommentsRatio ? Math.round(responsesToCommentsRatio) : 0

  return (
    <div className={props.className}>
      <MetricsCard title={t`Comments`} hasReducedPadding>
        <NumberMetric
          isLoading={isCommentsCountLoading || isCommentsCountDeltaLoading}
          value={overallCommentsCount}
          delta={overallCommentsCountDelta}
          deltaHint={t`Variation since previous survey`}
          deltaPolarity="positive"
        />
        <Caption>{t`Received comments`}</Caption>

        <NumberMetric
          isLoading={props.isLoading}
          value={props.averageCommentsCountPerQuestion}
          delta={averageCommentsCountPerQuestionDelta}
          deltaHint={t`Variation since previous survey`}
          deltaPolarity={props.averageCommentsCountPerQuestionTrend}
        />
        <Caption
          hint={t`${props.averageCommentsCountPerQuestionForLatestSurvey} in the latest survey`}
        >
          {t`Average comments count per question`}
        </Caption>

        <CompletionChart
          isLoading={
            isResponsesToCommentsRatioLoading || isResponsesToCommentsRatioDeltaLoading || isReponsesCountLoading
          }
          completionPercentage={responseToCommentRatio}
          delta={responsesToCommentsRatioDelta && Math.round(responsesToCommentsRatioDelta)}
          deltaHint={t`Variation since previous survey`}
          isDeltaFloating
          isSecondaryColor
        />
        <Caption
          hint={
            // Line break intended
            t`${overallCommentsCount} comments received,
            ${overallResponsesCount} responses sent`}
        >
          {t`Average response rate`}
        </Caption>
      </MetricsCard>
    </div>
  )
}

export interface CommentsMetricsProps extends BaseProps {
  /** Average number of comments per question */
  averageCommentsCountPerQuestion: number,

  /** Average number of comments for the latest survey */
  averageCommentsCountPerQuestionForLatestSurvey: number,

  /** Trend of the average number of comments per question
   * between the last survey and the previous one */
  averageCommentsCountPerQuestionTrend?: "positive" | "negative",

  isUserAllowedToReadComments: boolean,

  surveyContentType: SurveyContentType,
}
